// CUSTODY — WAITING ON YOU (his custody redesign, yes on 2 Nov 2026: four screens instead of seven; the drawings in
// docs/ui-rebuild/shots/custody-proposal/s1, s2).
//
// What needs him, one row each with its one action at the right of the row: a card to sign (a payout, a sweep, a refill, a
// Treasury or Flagged move) — Sign; a card refused on its face — Open; money in the Flagged wallet — Pay out; Hot short of
// the payouts waiting on it — Refill; balances not fully read — Read again. The desk window once, above. A held deposit (KYT not rated yet) is one quiet line, not a
// task. A strip of the floats underneath.
//
// Every row is drawn from a SUCCESSFUL read (DESIGN-SYSTEM Appendix B): a failed read is its own row with Try again, never
// "nothing waiting"; the all-clear line needs every read known. The rules are the wallet's own (cardActions, cardReady,
// waitsOnHim, poolTotal); nothing is decided here.
import { SigninQuiet } from '../../../components/v2/custody/SigninLine';
import { isCustodySignin } from '../../../lib/errors';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { custody as api, type ApprovalRow, type DepositRow } from '../../../lib/api';
import { cardActions, cardReady, poolTotal, usdtUnits, whitelistGroup } from '../../../lib/custody-screen';
import { shortAddress } from '../../../lib/custody-format';
import { timeLeft } from '../../../lib/custody-format';
import { dustFor } from '../../../lib/naming';
import { Panel, SkeletonRows, SummaryStrip, TAP } from '../../../components/v2/kit';
import { CustodyHead, CustodyTabs, CustodyActions, Figure, WindowLine, kindWord, clock } from '../../../components/v2/custody/parts';
import { waitsOnHim, cardPath } from '../../../components/v2/custody/deposit-words';

const who = (d: DepositRow) => d.partyName ?? d.partySlug ?? 'Untagged sender';
/** A deposit whose source was named without a figure (N-032): said on its row — he checks it by hand. */
const hand = (d: DepositRow) => (d.kytHandCheck ? <> · <span className="text-warn">Check by hand</span></> : null);
const unitsToDecimal = (u: bigint): string => `${u / 1_000_000n}${u % 1_000_000n ? `.${(u % 1_000_000n).toString().padStart(6, '0').replace(/0+$/, '')}` : ''}`;

/** One task: what, its figure line, and its one action at the right of the title line. */
function Row({ title, sub, action }: { title: React.ReactNode; sub?: React.ReactNode; action: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 border-b border-stroke-soft px-4 py-3 last:border-b-0 lg:px-5">
      <div className="min-w-0">
        <div className="line-clamp-2 break-words text-body font-medium">{title}</div>
        {sub && <div className="mt-1 text-meta text-tg-hint">{sub}</div>}
      </div>
      {action}
    </div>
  );
}

/** A read that failed, as one row: what, and Try again. */
function FailedRow({ what, retry, error }: { what: string; retry: () => void; error: unknown }) {
  // The lapsed custody sign-in is said once, above the screen (CustodySigninShell) — no row per read.
  if (isCustodySignin(error)) return null;
  return (
    <Row
      title={<span className="inline-flex items-center gap-2 text-neg"><AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />Couldn&apos;t read {what}</span>}
      action={<button type="button" className="btn-secondary whitespace-nowrap px-3 text-body" onClick={retry}>Try again</button>}
    />
  );
}

export function CustodyWaitingV2() {
  const approvals = useQuery({ queryKey: ['custody', 'approvals'], queryFn: api.approvals, refetchInterval: 15_000 });
  const deposits = useQuery({ queryKey: ['custody', 'deposits'], queryFn: () => api.deposits(), refetchInterval: 20_000 });
  const balances = useQuery({ queryKey: ['custody', 'balances'], queryFn: api.balances, refetchInterval: 30_000 });
  const limits = useQuery({ queryKey: ['custody', 'limits'], queryFn: api.limits, staleTime: 30_000, retry: false });
  // A PAYOUT ADDRESS WAITING FOR HIS APPROVAL is his to do (his "propsed address gets hidden in the web of addresses", 4 Nov 2026):
  // one row each, its Approve in the Books' register, opened on that row.
  const whitelist = useQuery({ queryKey: ['custody', 'whitelist'], queryFn: api.whitelist });
  const awaiting = (whitelist.data?.entries ?? []).filter((e) => whitelistGroup(e) === 'awaiting');

  const cards = approvals.data?.approvals ?? [];
  const live = cards.filter((a) => cardActions(a, timeLeft(a.expiresAt).expired).sign);
  const refused = cards.filter((a) => { const r = cardActions(a, false).reason; return r === 'incomplete' || r === 'unknown_payee'; });
  const deps = deposits.data?.deposits ?? [];
  // A sweep with a card is already one of the cards above (Approvals); one still waiting for the desk window is its own row.
  const sweepTxids = new Set(live.map((a) => a.txid));
  const readyNoCard = deps.filter((d) => cardReady(d.sweep) && !(d.sweep?.card?.txid && sweepTxids.has(d.sweep.card.txid)));
  const waiting = deps.filter(waitsOnHim);
  const held = deps.filter((d) => d.status === 'held');
  const preparing = deps.filter((d) => d.status === 'routed' && d.sweep?.status === 'activating').length;

  const wallets = balances.data?.wallets ?? [];
  const of = (cls: string) => wallets.filter((w) => w.class === cls);
  const hot = of('running_hot');
  const hotTotal = poolTotal(hot);
  const hotAddresses = new Set(hot.map((w) => w.address));
  // The shortfall only from a CURRENT read of both (Screens and Astra, 27 Oct 2026, #5).
  const payoutsKnown = approvals.isSuccess && !approvals.isError && balances.isSuccess && !balances.isError;
  const fromHot = payoutsKnown ? live.filter((a) => a.kind === 'payout' && hotAddresses.has(a.fromAddress) && a.amount !== null) : [];
  const fromHotSum = fromHot.reduce((s, a) => s + usdtUnits(a.amount!), 0n);
  const hotUnits = hotTotal.total !== null && hotTotal.read === hotTotal.all ? usdtUnits(hotTotal.total) : null;
  const short = hotUnits !== null && fromHotSum > hotUnits ? fromHotSum - hotUnits : null;
  // MONEY IN FLAGGED: the Flagged wallet holds something — today's way out is a payout from Flagged to a merchant's approved
  // payout address (Wallets). KYT never routes a deposit there (the wallet session, 2 Nov 2026); the wallet's figure says it.
  const flaggedTotal = poolTotal(of('frozen'));
  const inFlagged = balances.isSuccess && !balances.isError && flaggedTotal.total !== null && Number(flaggedTotal.total) > dustFor(of('frozen')[0]?.asset ?? 'USDT') ? flaggedTotal.total : null;

  // BALANCES NOT FULLY READ OR NOT CURRENT are a task of their own (Astra, 2 Nov 2026): a partial total says its coverage,
  // and the all-clear needs every balance read and fresh.
  const unread = wallets.filter((w) => w.balance === null).length;
  const notCurrent = balances.isSuccess && !balances.isError && wallets.length > 0 && (unread > 0 || balances.data!.freshness !== 'fresh');
  const reading = (!approvals.data && !approvals.isError) || (!deposits.data && !deposits.isError) || (!balances.data && !balances.isError);
  const allKnown = approvals.isSuccess && deposits.isSuccess && balances.isSuccess && whitelist.isSuccess && !approvals.isError && !deposits.isError && !balances.isError && !whitelist.isError;
  const count = allKnown ? live.length + refused.length + readyNoCard.length + waiting.length + awaiting.length + (short !== null ? 1 : 0) + (notCurrent ? 1 : 0) + (inFlagged !== null ? 1 : 0) : null;
  const nothing = count === 0;

  // ONE GOLD per screen: the first card to sign.
  let goldGiven = false;
  const signButton = (to: string) => {
    const gold = !goldGiven; goldGiven = true;
    return <Link to={to} className={`${gold ? 'btn-primary' : 'btn-secondary'} whitespace-nowrap px-4 text-body`}>Sign</Link>;
  };
  const cardTitle = (a: ApprovalRow) => `${kindWord(a.kind)} · ${a.partyName ?? (a.kind === 'sweep' ? 'Deposit' : 'Not named')}`;

  // The strip: the floats the tasks don't already say — Hot, Flagged, Fee.
  /** A float's figure and its coverage: a total of fewer wallets than it has says "N of M read" (never shown as complete). */
  const cover = (rows: typeof wallets) => { const t = poolTotal(rows); return t.read < t.all ? <span className="text-warn">{t.read} of {t.all} read</span> : null; };
  const strip = [
    ...(hot.length ? [{ label: 'Hot · TRC20', value: <Figure value={hotTotal.total} asset={hot[0]!.asset} />, sub: cover(hot) ?? (limits.data ? (limits.data.capsOff ? <span className="text-warn">No caps</span> : <>Per move <Figure value={limits.data.moveCeilingUsdt} asset="USDTTRC" plain /></>) : undefined) }] : []),
    ...(of('frozen').length ? [{ label: 'Flagged · TRC20', value: <Figure value={poolTotal(of('frozen')).total} asset={of('frozen')[0]!.asset} />, sub: cover(of('frozen')) ?? 'Moves on your decision' }] : []),
    ...(of('fee').length ? [{ label: 'Fee · TRC20', value: <Figure value={poolTotal(of('fee')).total} asset={of('fee')[0]!.asset} />, sub: cover(of('fee')) ?? 'Chain fees' }] : []),
  ];

  return (
    <div className="lg:max-w-[1160px]">
      <CustodyHead actions={<CustodyActions items={[{ label: 'New address', to: '/custody/deposits/new' }, { label: 'Move', to: '/custody/move' }, { label: 'Pay out', to: '/custody/pay' }]} />} />
      <CustodyTabs current="waiting" waiting={count} />

      <div className="mb-4 rounded-[10px] border border-stroke bg-panel px-4 py-2 lg:mb-5 lg:px-5"><WindowLine /></div>

      <Panel title="Needs you" aside={count !== null && count > 0 ? `${count} ${count === 1 ? 'item' : 'items'}` : undefined} flush className="mb-4 lg:mb-5">
        {reading && <SkeletonRows rows={3} what="what needs you" />}
        {approvals.isError && <FailedRow what="the approvals" error={approvals.error} retry={() => void approvals.refetch()} />}
        {approvals.isSuccess && live.map((a) => (
          <Row key={a.txid} title={cardTitle(a)}
            sub={<>{a.amount !== null ? <Figure value={a.amount} asset={a.asset} /> : 'Amount not read'} · by <span className="font-mono tabular">{clock(a.expiresAt)}</span>{a.approval?.required && !a.approval.at ? <> · <span className="text-warn">second device first</span></> : null}</>}
            action={signButton(`/custody/approvals/${a.txid}`)} />
        ))}
        {approvals.isSuccess && refused.map((a) => (
          <Row key={`r-${a.txid}`} title={<span className="text-neg">Do not sign · {a.partyName ?? 'Address not approved'}</span>}
            sub={a.amount !== null ? <Figure value={a.amount} asset={a.asset} /> : undefined}
            action={<Link to={`/custody/approvals/${a.txid}`} className="btn-secondary whitespace-nowrap px-3 text-body">Open</Link>} />
        ))}
        {deposits.isError && <FailedRow what="the deposits" error={deposits.error} retry={() => void deposits.refetch()} />}
        {deposits.isSuccess && readyNoCard.map((d) => (
          <Row key={`s-${d.id}`} title={`Sweep · ${who(d)}`} sub={<><Figure value={d.amount} asset={d.asset} />{hand(d)}</>} action={signButton(cardPath(d.sweep))} />
        ))}
        {deposits.isSuccess && waiting.map((d) => (
          <Row key={`w-${d.id}`} title={`Sweep · ${who(d)}`} sub={<><Figure value={d.amount} asset={d.asset} /> · <span className="text-warn">Needs desk window</span>{hand(d)}</>}
            action={<Link to={`/custody/deposits/${d.id}`} className="btn-secondary whitespace-nowrap px-3 text-body">Open</Link>} />
        ))}
        {whitelist.isError && <FailedRow what="the payout addresses" error={whitelist.error} retry={() => void whitelist.refetch()} />}
        {whitelist.isSuccess && awaiting.map((e) => (
          <Row key={`a-${e.id}`} title={`Payout address · ${e.partyName}`}
            sub={<><span className="font-mono">{shortAddress(e.address)}</span> · <span className="text-warn">Waiting for approval</span></>}
            action={<Link to={`/wallets?open=${e.id}`} className="btn-secondary whitespace-nowrap px-3 text-body">Approve</Link>} />
        ))}
        {balances.isError && <FailedRow what="the balances" error={balances.error} retry={() => void balances.refetch()} />}
        {inFlagged !== null && (
          <Row title="Money in Flagged" sub={<><Figure value={inFlagged} asset={of('frozen')[0]!.asset} />{flaggedTotal.read < flaggedTotal.all ? <> · <span className="text-warn">{flaggedTotal.read} of {flaggedTotal.all} read</span></> : null}</>}
            action={<Link to="/custody/wallets#payouts" className="btn-secondary whitespace-nowrap px-3 text-body">Pay out</Link>} />
        )}
        {short !== null && (
          <Row title="Hot short of waiting payouts" sub={<span className="text-warn">Short <Figure value={unitsToDecimal(short)} asset={hot[0]!.asset} /></span>}
            action={<Link to="/custody/refill" className="btn-secondary whitespace-nowrap px-3 text-body">Refill</Link>} />
        )}
        {notCurrent && (
          <Row title="Balances not current" sub={<span className="text-warn">{unread > 0 ? `${wallets.length - unread} of ${wallets.length} read` : 'Last read not fresh'}</span>}
            action={<button type="button" className="btn-secondary whitespace-nowrap px-3 text-body" onClick={() => void balances.refetch()}>Read again</button>} />
        )}
        {nothing && <p className="px-4 py-6 text-body text-tg-hint lg:px-5">Nothing needs you now.</p>}
      </Panel>

      {deposits.isSuccess && preparing > 0 && (
        <p className="mb-2 text-body text-tg-hint">Preparing {preparing} {preparing === 1 ? 'card' : 'cards'} — about a minute</p>
      )}
      {deposits.isSuccess && held.length > 0 && (
        <p className="mb-4 text-body text-tg-hint lg:mb-5">
          {held.length} held · not rated yet · <Link to="/custody/deposits?show=held" className={TAP}>Deposits</Link>
        </p>
      )}
      {strip.length > 0 && <SummaryStrip items={strip} cols={3} />}
    </div>
  );
}

/** Today's name, kept for the router and older links. */
export const CustodyOverviewV2 = CustodyWaitingV2;
